import { useEffect, useRef } from 'react';
import type BackendService from '../../services/admin/BackendService';
import type { ContentItem } from '../../models/ContentManagementInterface';
import type FundOpportunity from '../../models/FundOpportunity';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import type { ImportedPageRange } from '../admin/newContentManagement/documentImportLimits';
import { recordImportedBatch } from '../admin/newContentManagement/documentImportProgress';
import type { ImageCardsCropMeta } from '../admin/newContentManagement/useImageCardsSplit';

interface UploadDocumentDraftOptions {
  open: boolean;
  fundOpportunity: FundOpportunity;
  backendService: BackendService;
  createDraft: () => Promise<AdminFundUpdateEntry | null>;
  onClose: () => void;
  onImported: (draftId: string) => void;
}

/** Saves the draft's full imported content, plus the crop source for a single-page cards import. */
async function saveImportedContent(
  backendService: BackendService,
  fundOpportunity: FundOpportunity,
  draftId: string,
  content: ContentItem[],
  batch: { sections: ContentItem[]; cropMeta?: ImageCardsCropMeta | null },
) {
  await backendService.uploadContentManagementUpdate('FundUpdate', draftId, {
    name: `${fundOpportunity.name ?? 'Fund'} update`,
    relatedObject: 'FundUpdate',
    relatedObjectId: draftId,
    content,
  });
  const { sections, cropMeta } = batch;
  // A single-page import that detected cards: save its crop source so the draft's editor
  // (once opened) can restore and offer "Adjust crops" on the block this created — mirrors
  // appendImportedSections' own handling for an import into an already-open draft.
  if (cropMeta?.source && sections.length === 1 && sections[0].type === 'ImageCarousel') {
    await backendService
      .saveImageCardCropSource({
        modelType: 'FundUpdate',
        modelId: draftId,
        contentBlockId: sections[0].id,
        source: cropMeta.source,
        boxes: cropMeta.boxes,
      })
      .catch((err) => console.warn('Could not save the crop source for Adjust crops:', err));
  }
}

/**
 * The draft behind Upload Document: created on the first successful import, then every later
 * batch is appended and saved into the same draft. Errors are thrown back to the document viewer,
 * which shows them beside the Import button.
 */
export function useUploadDocumentDraft({
  open,
  fundOpportunity,
  backendService,
  createDraft,
  onClose,
  onImported,
}: UploadDocumentDraftOptions) {
  const importedDraft = useRef<AdminFundUpdateEntry | null>(null);
  const importedSections = useRef<ContentItem[]>([]);

  useEffect(() => {
    if (!open) {
      importedDraft.current = null;
      importedSections.current = [];
    }
  }, [open]);

  const prepareDraft = async (sections: ContentItem[]) => {
    if (sections.length === 0) {
      throw new Error('No content was found in this selection. Choose another page or range.');
    }
    const draft = importedDraft.current ?? (await createDraft());
    if (!draft) {
      throw new Error('Unable to prepare a draft for this import. Please try again.');
    }
    importedDraft.current = draft;
    return draft;
  };

  const importSections = async (sections: ContentItem[], cropMeta?: ImageCardsCropMeta | null) => {
    try {
      const draft = await prepareDraft(sections);
      const content = [...importedSections.current, ...sections];
      await saveImportedContent(backendService, fundOpportunity, draft.externalId, content, {
        sections,
        cropMeta,
      });
      importedSections.current = content;
    } catch (err) {
      console.error('[UploadDocumentDialog] failed to save imported content:', err);
      throw err;
    }
  };

  /** So the draft's own Docs tab resumes this document at its next batch. */
  const recordBatch = (documentId: string, range: ImportedPageRange) => {
    if (importedDraft.current) {
      recordImportedBatch('FundUpdate', importedDraft.current.externalId, documentId, range);
    }
  };

  const finish = () => {
    if (importedDraft.current && importedSections.current.length > 0) {
      onImported(importedDraft.current.externalId);
    } else {
      onClose();
    }
  };

  return { importSections, recordBatch, finish };
}
